import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumIntegrationType } from "./enums/enumIntegrationType.generated";

// Model Objects

export class BackgroundProcessingError extends ModelObject<BackgroundProcessingError> {

    /**
    * Which type of integration raised the error.
    */
    public integrationType: EnumIntegrationType = EnumIntegrationType.unknown;
    public id: Guid = Guid.createEmpty();
    /**
    * The Timestamp of the error.
    */
    public timestamp: Instant = Instant.ofEpochMilli(0);
    /**
    * The text of the error.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorText: string = "";
    /**
    * Id of the context on which the error occurred like WooCommerceSettingsId.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public contextId: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.integrationType = EnumIntegrationType.getByValue(jsonSerializedObject.integrationType, this.integrationType);
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.timestamp = Instant.parse(jsonSerializedObject.timestamp);
          this.errorText = jsonSerializedObject.errorText;
          this.contextId = jsonSerializedObject.contextId;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.integrationType = this.integrationType.value;
        toRet.id = this.id.toString();
        toRet.timestamp = this.timestamp.toString();
        toRet.errorText = this.errorText;
        toRet.contextId = this.contextId;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "BackgroundProcessingError.Id cannot be empty"},
    ];
}


export class FilteredBackgroundProcessingErrorsResult extends ModelObject<FilteredBackgroundProcessingErrorsResult> {

    public matchingCount: number = 0;
    public backgroundProcessingErrors: (BackgroundProcessingError)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.backgroundProcessingErrors = jsonSerializedObject.backgroundProcessingErrors.map((v: any) => new BackgroundProcessingError(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.backgroundProcessingErrors = this.backgroundProcessingErrors.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "backgroundProcessingErrors", rule: (v: any) => !!v || "backgroundProcessingErrors is required"},
            {field: "backgroundProcessingErrors", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as BackgroundProcessingError[]).reduce((acc: Array<string>, curr: BackgroundProcessingError) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class BackgroundProcessingErrorFilter extends ModelObject<BackgroundProcessingErrorFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _integrationTypes: readonly (EnumIntegrationType)[] | null = null;
    public get integrationTypes(): readonly (EnumIntegrationType)[] | null { return this._integrationTypes }
    public set integrationTypes(value: readonly (EnumIntegrationType)[] | null) {
      this._integrationTypes = value === undefined ? null : value;
    }
    private _fromTimestamp: Instant | null = null;
    /**
    * Minimum (inclusive) timestamp
    */
    public get fromTimestamp(): Instant | null { return this._fromTimestamp }
    public set fromTimestamp(value: Instant | null) {
      this._fromTimestamp = value === undefined ? null : value;
    }
    private _toTimestamp: Instant | null = null;
    /**
    * Maximum (inclusive) timestamp
    */
    public get toTimestamp(): Instant | null { return this._toTimestamp }
    public set toTimestamp(value: Instant | null) {
      this._toTimestamp = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.integrationTypes = jsonSerializedObject.integrationTypes == null ? null : jsonSerializedObject.integrationTypes.map((v: any) => EnumIntegrationType.getByValue(v));
          this.fromTimestamp = jsonSerializedObject.fromTimestamp == null ? null : Instant.parse(jsonSerializedObject.fromTimestamp);
          this.toTimestamp = jsonSerializedObject.toTimestamp == null ? null : Instant.parse(jsonSerializedObject.toTimestamp);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.integrationTypes = this.integrationTypes == null ? null : this.integrationTypes.map(v => v.value);
        toRet.fromTimestamp = this.fromTimestamp == null ? null : this.fromTimestamp.toString();
        toRet.toTimestamp = this.toTimestamp == null ? null : this.toTimestamp.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class BackgroundProcessingErrorService extends ServiceBase {

  public async getBackgroundProcessingErrorsByFilter(filter: BackgroundProcessingErrorFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredBackgroundProcessingErrorsResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredBackgroundProcessingErrorsResult(response.results);;
    return this.makeJsonRequest("backgroundProcessingError/v1/getBackgroundProcessingErrorsByFilter", req, false, extractor, opts);
  };

  public async getWooCommerceErrors(settingsId: Guid, maximumResults: number, fromTimestamp: Instant, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(BackgroundProcessingError)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    settingsId:settingsId.toString(),
    maximumResults:maximumResults,
    fromTimestamp:fromTimestamp.toString(),
    };
    const extractor = (response:any) => response.results.map((v: any) => new BackgroundProcessingError(v));;
    return this.makeJsonRequest("backgroundProcessingError/v1/getWooCommerceErrors", req, false, extractor, opts);
  };

  public async getIntegrateOnceErrors(settingsId: Guid, maximumResults: number, fromTimestamp: Instant, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(BackgroundProcessingError)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    settingsId:settingsId.toString(),
    maximumResults:maximumResults,
    fromTimestamp:fromTimestamp.toString(),
    };
    const extractor = (response:any) => response.results.map((v: any) => new BackgroundProcessingError(v));;
    return this.makeJsonRequest("backgroundProcessingError/v1/getIntegrateOnceErrors", req, false, extractor, opts);
  };
}

export const backgroundProcessingErrorService = new BackgroundProcessingErrorService();